import { compact, get, keyBy, uniq } from 'lodash';
import { DEFAULT_ERROR } from 'utils/constants';
import DatadogHandler from 'utils/datadog';
import {
  onboardingService,
  MS_PHASE_3_ENDPOINTS,
  centreSerivce,
} from 'utils/msEndpointConstants';
import {
  convertObjToCamelCase,
  retrieveDataMapper,
  retrieveObjectMapper,
  retrieveSortForMS,
} from 'utils';
import { transformProgramData } from '../utils/ms-utils/ms-phase-3/EnrolmentPlanningUtil';
import { getCentreByIds } from './CentreService';
import { getLevelByIds } from './SchoolConfigService';
import { getUserByIds } from './UserService';

const fileName = 'src/services/EnrolmentPlanningService.js';

const { CENTRE, ENROLMENT_PLANNING } = MS_PHASE_3_ENDPOINTS;

export const msGetEnrollmentPlansByYear = async (_, reqData) => {
  try {
    const onboardingSvc = onboardingService();
    const reqBody = {
      centre_id: get(reqData, 'centreID'),
      year: get(reqData, 'year'),
      level_id: get(reqData, 'levelID'),
      ...(get(reqData, 'programID') && {
        program_id: get(reqData, 'programID'),
      }),
      ...(get(reqData, 'from') && { from: get(reqData, 'from') }),
      ...(get(reqData, 'to') && { to: get(reqData, 'to') }),
      ...(typeof get(reqData, 'skip_hubspot') !== 'undefined' && {
        skip_hubspot: get(reqData, 'skip_hubspot'),
      }),
    };
    const resp = await onboardingSvc.post(
      ...ENROLMENT_PLANNING.getEnrolmentPlansByYear(reqBody)
    );

    const plans = get(resp, 'data.enrolment_plans', []);
    const transformed = plans.map(p => ({
      Program: (get(p, 'program', []) || []).map(pr =>
        retrieveObjectMapper(pr || {}, [
          'ID:id',
          'label',
          'fk_level',
          'type',
          'placement_order',
        ])
      ),
      TargetEnrollment: get(p, 'target_enrolment', []),
      VacanciesOfferedInPrinciple: get(p, 'vacancies_offered_in_principle', []),
      ConsumedCapacity: get(p, 'consumed_capacity', []),
      VacanciesOffered: get(p, 'vacancies_offered', []),
      RegistrationsInProcess: get(p, 'registrations_in_process', []),
      RegistrationsCompleted: get(p, 'registrations_completed', []),
      Enrollments: get(p, 'enrolments', []),
      Available: get(p, 'available', []),
      AvailableActual: get(p, 'available_actual', []),
      EstPromotionIn: get(p, 'est_promotion_in', []),
      EstPromotionOut: get(p, 'est_promotion_out', []),
      PromotionIn: get(p, 'promotion_in', []),
      PromotionOut: get(p, 'promotion_out', []),
      TransferIn: get(p, 'transfer_in', []),
      TransferOut: get(p, 'transfer_out', []),
      Withdrawal: get(p, 'withdrawal', []),
      WaitlistedHS: get(p, 'waitlisted_hs', []),
    }));

    return {
      success: true,
      data: { getEnrollmentPlansByYear: transformed },
    };
  } catch (error) {
    DatadogHandler.addError(error);
    DatadogHandler.sendLog(
      error,
      {
        functionName: 'msGetEnrollmentPlansByYear',
        fileName,
      },
      'error'
    );
    return {
      success: false,
      error: get(error, 'message', DEFAULT_ERROR),
    };
  }
};

export const msGetListEnrollmentPlanningRequests = async () => {
  try {
    const res = await centreSerivce().post(
      ENROLMENT_PLANNING.getListEnrollmentPlanningRequests,
      {}
    );
    const data = get(res, 'data.data') || [];
    const centreIds = uniq(data.map(item => item?.draft?.fk_centre));
    const levelIds = uniq(data.map(item => item?.draft?.fk_level));
    const userIds = uniq(data.map(item => item?.draft?.fk_user));
    const [centres, levels, users] = await Promise.all([
      getCentreByIds(centreIds),
      getLevelByIds(levelIds),
      getUserByIds(userIds),
    ]);
    const centreMap = keyBy(centres?.data, 'id');
    const levelMap = keyBy(levels, 'id');
    const userMap = keyBy(users, 'id');

    return {
      data: {
        listEnrollmentPlanningChangeRequests: data.map(item => ({
          Changes: item?.changes || [],
          Draft: {
            ...convertObjToCamelCase(item?.draft, [
              'fkProgramType:fk_program_type_string',
              'status:status_string',
            ]),
            centre: {
              ID: item?.draft?.fk_centre,
              label: centreMap?.[item?.draft?.fk_centre]?.label,
            },
            level: {
              ID: item?.draft?.fk_level,
              label: levelMap?.[item?.draft?.fk_level]?.label,
            },
            userByFkUser: {
              ID: item?.draft?.fk_user,
              firstname: userMap?.[item?.draft?.fk_user]?.firstname,
              lastname: userMap?.[item?.draft?.fk_user]?.lastname,
            },
          },
        })),
      },
      success: true,
    };
  } catch (error) {
    DatadogHandler.addError(error);
    DatadogHandler.sendLog(
      error,
      {
        functionName: 'msGetListEnrollmentPlanningRequests',
        fileName,
      },
      'error'
    );
    return {
      success: false,
      error: get(error, 'message', DEFAULT_ERROR),
    };
  }
};

export const msFindAllPrograms = async (_, filter) => {
  try {
    const centreSvc = centreSerivce();
    const body = {
      fk_school: [{ eq: filter?.fkSchool }],
    };
    const res = await centreSvc.post(...CENTRE.getAllPrograms(body));
    const transformedData = transformProgramData(res);
    return transformedData;
  } catch (error) {
    DatadogHandler.addError(error);
    DatadogHandler.sendLog(
      error,
      {
        functionName: 'msFindAllPrograms',
        fileName,
      },
      'error'
    );
    return {
      success: false,
      error: get(error, 'message', DEFAULT_ERROR),
    };
  }
};

export const msGetEnrolmentPlanningUtilisation = async (
  _,
  { centreID, year }
) => {
  try {
    const res = await centreSerivce().get(
      CENTRE.getEnrolmentPlanningUtilisation,
      {
        centreID,
        year,
      }
    );

    return {
      data: {
        getEnrolmentPlanningUtilisation: convertObjToCamelCase(
          res?.data,
          [],
          [
            'ICCapacity:ic_capacity',
            'ICTarget:ic_target',
            'CCCapacity:cc_capacity',
            'CCTarget:cc_target',
            'TTCapacity:tt_capacity',
            'TTTarget:tt_target',
            'Numerator:numerator',
            'Denominator:denominator',
          ]
        ),
      },
      success: true,
    };
  } catch (error) {
    DatadogHandler.addError(error);
    DatadogHandler.sendLog(
      error,
      {
        functionName: 'msGetEnrolmentPlanningUtilisation',
        fileName,
      },
      'error'
    );
    return {
      success: false,
      error: [
        {
          message: get(error, 'response.data.error', DEFAULT_ERROR),
        },
      ],
    };
  }
};

export const msGetListEnrollmentPlanningActivityLog = async (_, reqData) => {
  try {
    const resp = await centreSerivce().post(
      CENTRE.getListEnrollmentPlanningActivityLog,
      {
        ...(get(reqData, 'filter.fkCentre.length') && {
          centre_ids: get(reqData, 'filter.fkCentre'),
        }),
        ...(get(reqData, 'filter.fkLevel.length') && {
          level_ids: get(reqData, 'filter.fkLevel'),
        }),
        ...(get(reqData, 'filter.fkProgramType.length') && {
          fk_program_type: get(reqData, 'filter.fkProgramType'),
        }),
        exclude_empty_snap: get(reqData, 'filter.excludeEmptySnap', false),
        school_id: get(reqData, 'schoolID'),
        ...(get(reqData, 'filter.createdAt.length') && {
          created_at: get(reqData, 'filter.createdAt.0'),
        }),
      },
      {
        params: {
          page: get(reqData, 'pagination.page', 1),
          perPage: get(reqData, 'pagination.perPage', 10),
          ...retrieveSortForMS(get(reqData, 'pagination.sort', [])),
        },
      }
    );
    const data = get(resp, 'data.data') || [];
    const centreIds = uniq(compact(data.map(item => item?.fk_centre)));
    const levelIds = uniq(compact(data.map(item => item?.fk_level)));
    const userIds = uniq(
      compact([
        ...data.map(item => item?.fk_user),
        ...data.map(item => item?.fk_approver),
      ])
    );

    const [centres, levels, users] = await Promise.all([
      getCentreByIds(centreIds),
      getLevelByIds(levelIds),
      getUserByIds(userIds),
    ]);

    return {
      success: true,
      data: {
        capacityDraftsByFkSchool: {
          totalCount: get(resp, 'data.totalCount', 0),
          data: data.map(item => ({
            ...convertObjToCamelCase(
              item,
              [],
              [
                'ID:id',
                'fkProgramType:fk_program_type_string',
                'shadowStatus:shadow_status_string',
                'status:status_string',
              ]
            ),
            centre: retrieveDataMapper(centres?.data, item?.fk_centre, [
              'ID:id',
              'label',
            ]),
            level: retrieveDataMapper(levels, item?.fk_level, [
              'ID:id',
              'label',
            ]),
            userByFkUser: retrieveDataMapper(users, item?.fk_user, [
              'ID:id',
              'firstname',
              'lastname',
            ]),
            userByFkApprover: retrieveDataMapper(users, item?.fk_approver, [
              'ID:id',
              'firstname',
              'lastname',
            ]),
          })),
        },
      },
    };
  } catch (error) {
    DatadogHandler.addError(error);
    DatadogHandler.sendLog(
      error,
      {
        functionName: 'msGetListEnrollmentPlanningActivityLog',
        fileName,
      },
      'error'
    );
    return {
      success: false,
      error: get(error, 'message', DEFAULT_ERROR),
    };
  }
};

export const msApproveEnrollmentPlan = async ({ draftID }) => {
  try {
    const res = await centreSerivce().post(
      MS_PHASE_3_ENDPOINTS.ENROLMENT_PLANNING.approveEnrollmentPlan,
      {
        draft_id: draftID,
      }
    );
    return {
      data: {
        approveEnrollmentPlanningRequest: convertObjToCamelCase(res?.data),
      },
      success: res?.data?.success,
    };
  } catch (error) {
    DatadogHandler.addError(error);
    DatadogHandler.sendLog(
      error,
      {
        functionName: 'msApproveEnrollmentPlan',
        fileName,
      },
      'error'
    );
    return {
      success: false,
      error: get(error, 'message', DEFAULT_ERROR),
    };
  }
};

export const msRejectEnrollmentPlan = async ({ draftID, rejectRemark }) => {
  try {
    const res = await centreSerivce().post(
      MS_PHASE_3_ENDPOINTS.ENROLMENT_PLANNING.rejectEnrollmentPlan,
      {
        draft_id: draftID,
        rejection_remark: rejectRemark,
      }
    );
    return {
      data: {
        rejectEnrollmentPlanningRequest: convertObjToCamelCase(res?.data),
      },
      success: res?.data?.success,
    };
  } catch (error) {
    DatadogHandler.addError(error);
    DatadogHandler.sendLog(
      error,
      {
        functionName: 'msRejectEnrollmentPlan',
        fileName,
      },
      'error'
    );
    return {
      success: false,
      error: get(error, 'message', DEFAULT_ERROR),
    };
  }
};

export const msCreateEnrollmentPlanning = async reqData => {
  try {
    const { centreID, levelID, isValidatingExceeding, programID, update } =
      reqData || {};
    const { CENTRE } = MS_PHASE_3_ENDPOINTS;
    const centreSvc = centreSerivce();
    const res = await centreSvc.post(CENTRE.createEnrollmentPlanningRequest, {
      capacity_edit_request: update,
      centre_id: centreID,
      is_validate_exceeding: isValidatingExceeding,
      level_id: levelID,
      program_id: programID,
    });

    return {
      data: {
        createEnrollmentPlanningRequest: {
          ExceedingCapacities: res?.data?.exceeding_capacities.map(
            capacity => ({
              IsInfant: capacity.is_infant,
              IsExceedingCapacity: capacity.is_exceeding_capacity,
              TotalTargetEnrolment: capacity.total_target_enrolment,
              LicensedCapacity: capacity.licensed_capacity,
              Year: capacity.year,
              Month: capacity.month,
            })
          ),
        },
      },
      success: true,
    };
  } catch (error) {
    DatadogHandler.addError(error);
    DatadogHandler.sendLog(
      error,
      {
        functionName: 'msCreateEnrollmentPlanning',
        fileName,
      },
      'error'
    );
    return {
      success: false,
      error: get(error, 'message', DEFAULT_ERROR),
    };
  }
};
